import { immer } from "zustand/middleware/immer";
import { createStore } from "zustand/vanilla";

import type { LiveQuote } from "~/v3-poc/api/snapshotStream";

export type QuoteTick = "up" | "down" | "flat";

export interface StoredQuote {
  price?: number;
  previousClose?: number;
  tick: QuoteTick;
}

export interface QuoteStoreState {
  quotes: Record<string, StoredQuote>;
  applyQuotes: (incoming: Record<string, LiveQuote>) => void;
  clearQuotes: () => void;
}

const tickFor = (
  previous: number | undefined,
  next: number | undefined,
): QuoteTick => {
  if (previous === undefined || next === undefined || next === previous) {
    return "flat";
  }
  return next > previous ? "up" : "down";
};

/**
 * Merges an incoming frame onto the stored entry rather than replacing it: a
 * trade tick carries only `price`, and dropping the existing `previousClose`
 * would blank the watchlist's change-percent column until the next snapshot.
 *
 * Tick is a per-symbol decision, not a per-update one: a symbol's tick is the
 * direction of *that symbol's own* last actual price change, and it persists
 * until that symbol's price changes again. It is deliberately not reset by
 * another symbol ticking, nor by an update that carries no price for this
 * symbol — this replaces an earlier connector-side recomputation that rebuilt
 * every symbol's tick from scratch on each stream update, which flickered a
 * symbol's arrow off as soon as any unrelated symbol moved.
 */
const mergeQuote = (
  previous: StoredQuote | undefined,
  incoming: LiveQuote,
): StoredQuote => ({
  price: incoming.price ?? previous?.price,
  previousClose: incoming.previousClose ?? previous?.previousClose,
  tick:
    incoming.price === undefined
      ? (previous?.tick ?? "flat")
      : tickFor(previous?.price, incoming.price),
});

/**
 * High-frequency quote state, deliberately separate from the workspace store so
 * a price tick cannot invalidate a document selector. One store per provider
 * mount, matching `createWorkspaceStore`.
 */
export const createQuoteStore = () =>
  createStore<QuoteStoreState>()(
    immer((set) => {
      const applyQuotes = (incoming: Record<string, LiveQuote>) =>
        set((draft) => {
          for (const [symbol, quote] of Object.entries(incoming)) {
            draft.quotes[symbol] = mergeQuote(draft.quotes[symbol], quote);
          }
        });

      /** For an account switch: the old account's prices are not this one's. */
      const clearQuotes = () =>
        set((draft) => {
          draft.quotes = {};
        });

      return {
        quotes: {},
        applyQuotes,
        clearQuotes,
      };
    }),
  );

export type QuoteStore = ReturnType<typeof createQuoteStore>;

/** Narrows the store to one widget's symbols; pair with `useShallow`. */
export const pickQuotes = (
  quotes: Record<string, StoredQuote>,
  symbols: readonly string[],
): Record<string, StoredQuote> => {
  const picked: Record<string, StoredQuote> = {};
  for (const symbol of symbols) {
    const quote = quotes[symbol];
    if (quote !== undefined) {
      picked[symbol] = quote;
    }
  }
  return picked;
};
